import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  List,
  Alert,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { productService } from '../../../services/productService';
import type { Product } from '../../../types/product';
import type { ProductStockDistribution } from '../../../types/stock';

export default function ProductDetailPage() {
  const { id, productId: productIdParam } = useParams<{ id: string; productId: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;
  const productId = productIdParam ? parseInt(productIdParam) : null;

  // Data
  const [product, setProduct] = useState<Product | null>(null);
  const [stockDistribution, setStockDistribution] = useState<ProductStockDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!companyId || !productId) {
        setError('Ідентифікатор продукту відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [productData, availability] = await Promise.all([
          productService.getById(productId),
          productService.getAvailability(companyId, [productId]),
        ]);
        if (cancelled) {
          return;
        }
        setProduct(productData);
        setStockDistribution(availability[0]?.stockDistribution ?? []);
        setError(null);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Error fetching product details:', err);
        setError('Не вдалося завантажити дані продукту');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [companyId, productId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error ?? 'Продукт не знайдено'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/products`)}
          size="small"
        >
          Назад
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {product.name}
        </Typography>
      </Box>

      {product.code && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Код: {product.code}
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Наявність на складах
      </Typography>

      {stockDistribution.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            textAlign: 'center',
            backgroundColor: 'background.default',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Продукт відсутній на всіх складах
          </Typography>
        </Paper>
      ) : (
        <List>
          {stockDistribution.map((dist) => (
            <Paper
              key={dist.stockId}
              elevation={1}
              sx={{
                mb: 2,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body1">{dist.stockName}</Typography>
              </Box>
              <Chip
                label={`${dist.quantity} шт`}
                size="small"
                color={dist.quantity > 0 ? 'success' : 'default'}
                variant="outlined"
              />
            </Paper>
          ))}
        </List>
      )}
    </Box>
  );
}
